import type { EntityData } from '@adornis/baseql/entities/types.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { CountryPickerInput } from '@adornis/internationalization/client/language-picker/CountryPickerInput.js';
import { Language } from '@adornis/translation-core/languages.js';
import { and } from '@adornis/validation/functions/and.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { sub } from 'date-fns';
import { html, nothing } from 'lit';
import { Gender, Konfession, NamensTitel, Religion } from '../../../db/enums/misc-enums.js';
import { Namensvorsatz } from '../../../db/enums/namensvorsatz.js';
import { Namenszusatz } from '../../../db/enums/namenszusatz.js';
import { hasRoles, type HasRolesOptions } from '../../../permissions/api/hasRoles.js';
import { EMPLOYEE_ROLES, SBS_ROLES, STUDENT_ROLES } from '../../../permissions/roles.js';
import type { Person } from '../../entities/person.js';

/**
 * The church tax Konfession only matters for the LODAS export of employees, students and guardians get the edoo.sys
 * Religion instead. Someone in both keeps both, everybody else keeps the Konfession as before.
 */
export const getReligionFields = (person: HasRolesOptions['user']) => {
  const religion = hasRoles({ user: person, roles: [...STUDENT_ROLES, ...SBS_ROLES] });
  return { konfession: !religion || hasRoles({ user: person, roles: EMPLOYEE_ROLES }), religion };
};

export const StammdatenForm = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  /**
   * Check if the user is a SbS in any way. In this case we actiavte the Beruf field
   */
  const personIsSbS = hasRoles({ user: props.form.document, roles: SBS_ROLES });
  const religionFields = getReligionFields(props.form.document);

  return html`
    <div class="form-grid">
      ${FormGridEntry({
        label: 'Titel',
        render: DropdownSelectionInput({
          ...props.form.field('profilDaten', 'stammdaten', 'titel'),
          selectables: Object.values(NamensTitel),
          placeholder: 'Kein Titel hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Vorname',
        render: TextInput({
          ...props.form.field('profilDaten', 'stammdaten', 'vorname'),
          placeholder: 'Kein Vorname hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Nachname',
        render: TextInput({
          ...props.form.field('profilDaten', 'stammdaten', 'nachname'),
          placeholder: 'Kein Nachname hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Telefon',
        render: TextInput({
          ...props.form.field('profilDaten', 'stammdaten', 'telefonNr'),
          placeholder: 'Keine Telefonnummer hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Namenszusatz',
        render: DropdownSelectionInput({
          ...props.form.field('profilDaten', 'stammdaten', 'namenszusatz'),
          placeholder: 'Kein Namenszusatz hinterlegt',
          placeholderMode: 'hide',
          selectables: Object.values(Namenszusatz),
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Namensvorsatz',
        render: DropdownSelectionInput({
          ...props.form.field('profilDaten', 'stammdaten', 'vorsatzWort'),
          placeholder: 'Kein Namensvorsatz hinterlegt',
          placeholderMode: 'hide',
          selectables: Object.values(Namensvorsatz),
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Geburtsdatum',
        render: DateInput({
          ...props.form.field('profilDaten', 'stammdaten', 'geburtsDatum'),
          placeholder: 'Kein Geburtsdatum hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Geschlecht',
        render: DropdownSelectionInput({
          ...props.form.field('profilDaten', 'stammdaten', 'geschlecht'),
          placeholder: 'Kein Geschlecht hinterlegt',
          placeholderMode: 'hide',
          selectables: Object.values(Gender),
          clearable: true,
        }),
      })}
      ${
        religionFields.konfession
          ? FormGridEntry({
              label: 'Konfession',
              render: DropdownSelectionInput({
                ...props.form.field('profilDaten', 'stammdaten', 'konfession'),
                placeholder: 'Keine Konfession hinterlegt',
                placeholderMode: 'hide',
                selectables: Object.values(Konfession),
                clearable: true,
              }),
            })
          : nothing
      }
      ${
        religionFields.religion
          ? FormGridEntry({
              label: 'Religion',
              render: DropdownSelectionInput({
                ...props.form.field('profilDaten', 'stammdaten', 'religion'),
                placeholder: 'Keine Religion hinterlegt',
                placeholderMode: 'hide',
                selectables: Object.values(Religion),
                clearable: true,
              }),
            })
          : nothing
      }
      ${FormGridEntry({
        label: 'Geburtsland',
        render: CountryPickerInput({
          ...props.form.field('profilDaten', 'stammdaten', 'geburtsLand'),
          language: Language.GERMAN,
          showFlags: true,
          placeholder: 'Kein Geburtsland hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Geburtsort',
        render: TextInput({
          ...props.form.field('profilDaten', 'stammdaten', 'geburtsOrt'),
          placeholder: 'Kein Geburtsort hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Staatsangehörigkeit',
        render: CountryPickerInput({
          ...props.form.field('profilDaten', 'stammdaten', 'staatsangehoerigkeit'),
          language: Language.GERMAN,
          showFlags: true,
          placeholder: 'Keine Staatsangehörigkeit hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Zweite Staatsangehörigkeit',
        render: CountryPickerInput({
          ...props.form.field('profilDaten', 'stammdaten', 'staatsangehoerigkeit2'),
          language: Language.GERMAN,
          showFlags: true,
          placeholder: 'Keine zweite Staatsangehörigkeit hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${
        personIsSbS
          ? FormGridEntry({
              label: 'Beruf',
              render: TextInput({
                ...props.form.field('profilDaten', 'stammdaten', 'beruf'),
                placeholder: 'Kein Beruf hinterlegt',
                placeholderMode: 'hide',
                clearable: true,
              }),
            })
          : nothing
      }
    </div>
  `;
});

export const StammdatenFormValidator = (required: ReturnType<typeof getReligionFields>) => ({
  vorname: sgNonOptional(),
  nachname: sgNonOptional(),
  geburtsLand: sgNonOptional(),
  geburtsOrt: sgNonOptional(),
  staatsangehoerigkeit: sgNonOptional(),
  geburtsDatum: and(sgNonOptional(), v => {
    if (v.target.geburtsDatum >= sub(new Date(), { years: 5 }))
      throw new Error('Das Geburtsdatum muss mind. 5 Jahre in der Vergangenheit liegen.');
  }),
  geschlecht: sgNonOptional(),
  ...(required.konfession ? { konfession: sgNonOptional() } : {}),
  ...(required.religion ? { religion: sgNonOptional() } : {}),
});
